import {LitElement, html, css} from 'lit';
import {msg} from '@lit/localize';
import {customElement} from 'lit/decorators/custom-element.js';
import {repeat} from 'lit/directives/repeat.js';
import {classMap} from 'lit/directives/class-map.js';
import {connect} from 'pwa-helpers';
import SunCalc from 'suncalc';
import store from '../../store';
import {lightProfiles} from '../lightProfiles';
import {selectColorTemperature, selectProfileId, selectSpcConfiguration} from '../../selectors';
import {setChannelId} from '../lightProfileSlice';
import {pimText} from '../../util/values';


@customElement('light-profile-parameters')
export class LightProfileParameters extends connect(store)(LitElement) {

	static styles = [
		css`
		  :host {
		    display: block;
    		top: 15%;
    		position: relative;
    		width: 100%;
		  }

		  table.paramTable {
			border: 1px solid rgba(255, 255, 255, 0.2);
			border-collapse: collapse;
			text-align: left;
			width: 250px;
			margin-bottom: 200px;
			right: 0px;
    		left: var(--large-padding);
			position: absolute;
		  }

		  table.paramTable td {
			border: 1px solid rgba(255, 255, 255, 0.2);
			padding: 10px;
			width: 50%;
			min-width: 125px;
		  }

		  table.paramTable td.noBorder {
			border-left: none;
			border-right: none;
		  }

		  table.paramTable td.noTopBorder {
			border-top: none;
			padding-top: 0;
		  }

		  table.paramTable td.noBottomBorder {
			border-bottom: none;
			padding-bottom: 0;
		  }

		  p.paramTitle {
			color: var(--color-text-sections);
			font: var(--font-summary-table);
			margin: 5px;
		  }

		  p.paramValue {
			color: var(--color-white);
			font: var(--font-summary-table);
			margin: 5px;
		  }


		  .paramContainer {
			display: block;
			position: relative;
			margin-bottom: 12px;
			cursor: pointer;
			font-size: 22px;
			-webkit-user-select: none;
			-moz-user-select: none;
			-ms-user-select: none;
			user-select: none;
		  }

		  .paramContainer input {
			position: absolute;
			opacity: 0;
			cursor: pointer;
		  }

		  .checkmark {
			position: absolute;
			top: 0;
			left: 0;
			height: 10px;
			width: 10px;
			background-color: #000;
			border: 1px solid white;
			border-radius: 50%;
		  }

		  .checkmark.checked {
			background-color: #fff;
		  }

		  .paramContainer:hover input ~ .checkmark {
			background-color: #808080;
		  }

		  .paramContainer input:checked ~ .checkmark {
			background-color: black;
			border: 1px solid white;
		  }

		  .checkmark:after {
			content: "";
			position: absolute;
			display: none;
		  }

		  .paramContainer input:checked ~ .checkmark:after {
			display: block;
		  }

		  .paramContainer .checkmark:after {
			width: 10px;
			height: 10px;
			border-radius: 50%;
			background: white;
		  }

		  .onoffswitch {
			position: relative;
			width: 23px;
			-webkit-user-select: none;
			-moz-user-select: none;
			-ms-user-select: none;
		  }

		  .onoffswitchCheckbox {
			position: absolute;
			opacity: 0;
			pointer-events: none;
		  }

		  .onoffswitchLabel {
			display: block;
			overflow: hidden;
			cursor: pointer;
			border: 2px solid #000000;
			border-radius: 50px;
		  }

		  .onoffswitchInner {
			display: block;
			width: 200%;
			margin-left: -100%;
			transition: margin 0.3s ease-in 0s;
		  }

		  .onoffswitchInner:before, .onoffswitchInner:after {
			display: block;
			float: left;
			width: 50%;
			height: 1px;
			padding: 0;
			line-height: 1px;
			font-size: 14px;
			color: white;
			font-family: Trebuchet, Arial, sans-serif;
			font-weight: bold;
			box-sizing: border-box;
		  }

		  .onoffswitchInner:before {
			content: "";
			padding-left: 10px;
			background-color: #FFFFFF;
			color: #FFFFFF;
		  }

		  .onoffswitchInner:after {
			content: "OFF";
			padding-right: 10px;
			background-color: #EEEEEE;
			color: #FFFFFF;
			text-align: right;
		  }

		  .onoffswitchSwitch {
			display: block;
			width: 9px;
			margin: -4px;
			background: #FFFFFF;
			position: absolute;
			top: 0;
			bottom: 0;
			right: 18px;
			border: 2px solid #000000;
			border-radius: 50px;
			transition: all 0.3s ease-in 0s;
		  }

		  .onoffswitchCheckbox:checked + .onoffswitchLabel .onoffswitchInner {
			margin-left: 0;
		  }

		  .onoffswitchCheckbox:checked + .onoffswitchLabel .onoffswitchSwitch {
			right: 0px;
		  }

		  @media (max-width: 768px) {
			table.paramTable {
			  border-collapse: collapse;
			  float: none;
			  text-align: left;
			  width: 80%;
			  margin-bottom: 300px;
			  position: relative;
			}
			:host {
			width: 100%;
			}
		  }
		`
	];

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}
	connectedCallback() {
		super.connectedCallback();
		this.connected = true;
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.connected = false;
	}

	stateChanged(state) {
		this.state = state;
		this.translations = state.productData.translations;
		this.spcConfiguration = selectSpcConfiguration(state);
		this.profile = state.lightProfile.data.profile;
		this.lightProfile = state.lightProfile.data;
		this.lang = state.status.lang;
		this.selectedDay = state.lightProfile.data.selectedDay;
		this.selectedSecond = state.lightProfile.data.selectedSecond;
		this.userProfile = state.userProfile.data;

		const profileId = selectProfileId(state);
		if (profileId) {
			const lightProfile = lightProfiles.find(lp => lp.id === profileId);
			this.renderChannels = lightProfile?.parameterChannels ?? lightProfile?.renderChannels;
			this.layers = lightProfile?.layers;
		}

	}
	setTarget(renderChannelId) {
		store.dispatch(setChannelId(
			{
				channelId: renderChannelId
			}
		));
	}
	getKeyframe(keyframes, selectedDay, selectedSecond) {
		const date = new Date(2024, 0, selectedDay, 0, 0, selectedSecond + 3600*12 - this.lightProfile.project.location.offset * 60);

		const localdatestring = date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium' });
		const locald = new Date(Date.parse(localdatestring));

		const timestamp = locald.getTime();
		const keyframe = keyframes.find(kf => kf[0]*1000 > timestamp && kf[0]*1000 < timestamp + 3600*12*1000 && kf[0]*1000 > timestamp - 3600*12*1000);

		// find the element before keyframe in the array
		const index = keyframes.indexOf(keyframe);
		return keyframes[index - 1];
	}

	getIntensity(intensity) {
		if (!this.userProfile.loggedIn) {
			if (intensity === 0) {
				return '---';
			} else if (intensity < 33 ) {
				return msg('low');
			}  else if (intensity < 66) {
				return msg('medium');
			} else {
				return msg('high');
			}
		} else {
			return intensity + ' %';
		}
	}
	getTemperature(temperature) {
		if (!this.userProfile.loggedIn) {
			if (temperature === 0 || temperature === '0') {
				return '---';
			} else if (temperature === 'red' ) {
				return msg('red');
			} else if (temperature <= 2400 || temperature <= '2400K' ) {
				return msg('warm');
			} else if (temperature <= 2900 || temperature <= '2900K' ) {
				return msg('normal');
			}  else {
				return msg('cold');
			}
		} else {
			return temperature;
		}
	}

	render() {
		if (!this.lightProfile.simulationResult || !this.connected) return html``;
		const s = this.state;

		const date = new Date(2024, 0, this.selectedDay, 12, 0, this.selectedSecond);
		const dateStr = date.toLocaleDateString(this.lang, {month: 'long', day: 'numeric', hours12: false}).toUpperCase();
		const timeStr = date.toLocaleTimeString(this.lang, {hours12: false}).toUpperCase();

		const suntime = SunCalc.getTimes(date, this.lightProfile.project.location.latitude, this.lightProfile.project.location.longitude);
		const sunriseTs = Date.parse(suntime.sunrise.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium', timeZone: this.state.lightProfile.data.project.location.timezoneName, hours12: false }));
		const sunsetTs = Date.parse(suntime.sunset.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium', timeZone: this.state.lightProfile.data.project.location.timezoneName, hours12: false }));
		const sunrise = new Date(sunriseTs).toLocaleTimeString(this.lang, {hours12: false}).toUpperCase();
		const sunset = new Date(sunsetTs).toLocaleTimeString(this.lang, {hours12: false}).toUpperCase();
		const lightDistribution = pimText(this.lang, 'LE_LightDistribution_1', 'FV.S', this.spcConfiguration, this.state.productData.translations);
		const lightDistributionParts = lightDistribution.split(' + ');

		const parameters = [];
		// console.log('renderChannels', this.renderChannels);
		this.renderChannels.forEach(renderChannel => {
			let displayIntensity = 0;
			let displayTemperature = '---';
			//console.log(renderChannel, this.layers);
			renderChannel.layers.forEach(layer => {
				const keyframe = this.getKeyframe(this.lightProfile.simulationResult.targets[layer].keyframes, this.selectedDay, this.selectedSecond);
				let ctt = 0;
				if ( keyframe && keyframe.length === 4) {
					ctt = this.getTemperature(keyframe[3]);
				} else {
					if (this.lightProfile.project.base === 'AdjustingWhite' ||
						this.lightProfile.project.base === 'DynamicAmbience' ||
						this.lightProfile.project.base === 'DynamicPath') {
						const colorTemperatureFromLamp = selectColorTemperature(this.state) ?? 'standard';
						ctt = selectColorTemperature(this.state) ?? 'standard';
					} else {
						ctt = this.layers[layer].ctt;
					}
				}
				// const ctt = keyframe ? (keyframe.length === 4 ? keyframe[3] : this.layers[layer].ctt) : 0;
				const inten = keyframe ? (keyframe[2] * 100) : 0;
				displayIntensity = Math.max(inten, displayIntensity);
				displayTemperature = inten > 0 ? ctt : displayTemperature;
			});
			parameters[renderChannel.id] = {
				intensity: displayIntensity,
				temperature: displayTemperature
			};
		});
		return html`
			<table class="paramTable">
				<tbody>
				<tr>
					<td>
						<p class="paramTitle">${msg('Date')}</p>
						<p class="paramValue">${dateStr}</p>
					</td>
					<td>
						<p class="paramTitle">${msg('Time')}</p>
						<p class="paramValue">${timeStr}</p>
					</td>
				</tr>
				<tr>
					<td class="noBorder">
						<p class="paramTitle">${msg('Sunrise')}</p>
						<p class="paramValue">${sunrise}</p>
					</td>
					<td class="noBorder">
						<p class="paramTitle">${msg('Sunset')}</p>
						<p class="paramValue">${sunset}</p>
					</td>
				</tr>
				<tr>
					<td class="noBorder noBottomBorder">
						<p class="paramTitle">${msg('Light distribution')}</p>
					</td>
					<td class="noBorder noBottomBorder">
						<p class="paramTitle">${msg('Light intensity')}<br>${msg('Light color')}</p>
					</td>
				</tr>
				${repeat([...this.renderChannels],
					(renderChannel) => renderChannel.id,
					(renderChannel, index) => html`
					<tr>
						<td class="noBorder noTopBorder noBottomBorder">
							<div class="paramContainer" @click="${() => this.setTarget(renderChannel.id)}"'>
								<p class="paramValue">${lightDistribution !== '' ? (lightDistributionParts[index] ?? renderChannel.label) : msg('Channel') + ' ' + index}</p>
								<!-- <span class="checkmark ${classMap({checked: this.lightProfile.channelId === renderChannel.id})}"></span> -->
							</div>
						</td>
						<td class="noBorder noTopBorder noBottomBorder">
							<p class="paramValue">
								${this.getIntensity(parameters[renderChannel.id].intensity)}<br>
								${parameters[renderChannel.id].temperature}</p>
						</td>
					</tr>
				`)}
				</tbody>
			</table>
		`;
	}
}
